// Collect everything needed to style a raster on the map, based on
// its file descriptor (and layer inside the file).
// Visibility and opacity are taken from the layer directly and not in this hook.
// Actual rendering of the layer is in components/publication-map-raster-layers.tsx .

import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

import { Raster } from "@nens/lizard/api-v4";
import { FileDescriptorRead } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";
import { getWmsRequest } from "@nens/react-map-gl/utils";
import { Extent, convertLizardBoundsToMapExtent } from "@nens/shared-utils";

import { getFileDescriptorViewQuery } from "@/queries/query-file-descriptors";

import { applyRdcStyling } from "..";
import { getId } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { RasterLayer } from "../../../types/publication";

type RasterStylingOptions = {
  bounds: Extent | null;
  tiles: string /* Wms Request */;
  wmsLayer: string;
  styles: string | undefined;
} | null;

export function useRasterStylingOptions(
  descriptor: FileDescriptorRead,
  layer: RasterLayer
): RasterStylingOptions {
  const { tenant } = useContext(TenantContext);
  const rasterQuery = useQuery(getFileDescriptorViewQuery(descriptor, "lizard-raster"));
  const publicationId = usePublicationStore(getId);

  if (rasterQuery.isSuccess) {
    // Get bounds and WMS URL from the Lizard raster
    const raster: Raster = rasterQuery.data;
    const bounds = convertLizardBoundsToMapExtent(raster.spatial_bounds);

    const wmsUrl = `/v1-alpha/tenants/${tenant}/publications/${publicationId}/file-descriptors/${descriptor.id}/wms`;
    const wmsLayer = raster.wms_info.layer;
    const defaultStyles = applyRdcStyling(raster.options.styles, layer.properties.styleId);
    const minMax = layer.properties.minMax;
    const styles =
      minMax && defaultStyles
        ? `${defaultStyles.split(":")[0]}:${minMax[0]}:${minMax[1]}`
        : defaultStyles;

    const tiles = getWmsRequest(wmsUrl, {
      layer: wmsLayer,
      styles: styles,
      transparent: false,
    });

    return {
      bounds,
      tiles,
      wmsLayer,
      styles,
    };
  } else {
    return null;
  }
}
